import { useState } from 'react';

import { Bell, Info, Mail } from 'lucide-react';
import { toast } from 'sonner';

// ─── Types ──────────────────────────────────────────────────────

export interface NotificationCategory {
  id: string;
  label: string;
  description: string;
  email: boolean;
}

interface NotificationPreferencesPanelProps {
  categories: NotificationCategory[];
  accentColor?: string;
}

// ─── Toggle ─────────────────────────────────────────────────────

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 ${
        checked ? 'bg-emerald-600' : 'bg-slate-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ─── Panel ──────────────────────────────────────────────────────

export default function NotificationPreferencesPanel({
  categories: initialCategories,
  accentColor = 'emerald',
}: NotificationPreferencesPanelProps) {
  const [categories, setCategories] = useState(initialCategories);

  const iconBg = `bg-${accentColor}-100`;
  const iconText = `text-${accentColor}-600`;

  function updateCategory(id: string, field: 'email', value: boolean) {
    setCategories(prev => prev.map(c => (c.id === id ? { ...c, [field]: value } : c)));
    toast.success('Preference mise a jour');
  }

  return (
    <div className='space-y-6 max-w-2xl'>
      {/* Header card */}
      <div className='bg-white rounded-2xl p-6 border border-slate-200'>
        <div className='flex items-center gap-3 mb-6'>
          <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
            <Bell className={`w-5 h-5 ${iconText}`} />
          </div>
          <div>
            <h3 className='font-bold text-slate-900'>Preferences de notifications</h3>
            <p className='text-sm text-slate-500'>Choisissez comment vous souhaitez etre notifie</p>
          </div>
        </div>

        {/* Column headers */}
        <div className='flex items-center gap-4 mb-4 pb-3 border-b border-slate-100'>
          <div className='flex-1' />
          <div className='flex items-center gap-1 w-16 justify-center' title='Notifications par email'>
            <Mail className='w-4 h-4 text-slate-400' />
            <span className='text-xs font-semibold text-slate-500'>Email</span>
          </div>
        </div>

        {/* Notification rows */}
        <div className='space-y-1'>
          {categories.map(cat => (
            <div
              key={cat.id}
              className='flex items-center gap-4 py-3 px-2 rounded-lg hover:bg-slate-50 transition-colors'
            >
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-semibold text-slate-700'>{cat.label}</p>
                <p className='text-xs text-slate-400 mt-0.5'>{cat.description}</p>
              </div>
              <div className='w-16 flex justify-center'>
                <Toggle checked={cat.email} onChange={v => updateCategory(cat.id, 'email', v)} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info banner */}
      <div className='flex items-start gap-2 text-xs text-slate-400 px-1'>
        <Info className='w-4 h-4 shrink-0 mt-0.5' />
        <span>
          Les preferences sont enregistrees localement. La synchronisation avec le serveur sera disponible
          prochainement.
        </span>
      </div>
    </div>
  );
}
